import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { getEvalDashboardOptions } from '@/api/generated/@tanstack/react-query.gen';
import type { EvalFixtureCard } from '@/api/generated/types.gen';
import { Skeleton } from '@/components/ui/skeleton';
import { EvalShell, Panel, useEvaluatorName } from '@/components/Evals/EvalShell';
import { Delta, pct, scoreTone } from '@/components/Evals/format';
import { NewExperimentDialog } from '@/components/Evals/NewExperimentDialog';
import { LiveRunTile } from '@/components/Evals/LiveRunTile';
import { ScoreHistoryChart } from '@/components/Evals/ScoreHistoryChart';
import { cn } from '@/lib/utils';
import { Cover } from '@/components/Evals/Cover';

export const Route = createFileRoute('/_auth/evals/')({
  component: EvalDashboardPage,
});

function Stat({ label, value, sub, to }: { label: string; value: string; sub?: string; to?: string }) {
  const body = (
    <div className="h-full rounded-2xl bg-white px-4 py-3 ring-1 ring-black/5 transition hover:ring-black/10">
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-400">{label}</p>
        <p className="text-2xl font-semibold leading-tight tracking-tight text-neutral-900">{value}</p>
        <p className="truncate text-[12px] text-neutral-500">{sub ?? '\u00a0'}</p>
      </div>
    </div>
  );
  return to ? (
    <Link to={to} className="block h-full">
      {body}
    </Link>
  ) : (
    body
  );
}

function EvalDashboardPage() {
  const query = useQuery({
    ...getEvalDashboardOptions(),
    refetchInterval: (q) => ((q.state.data?.active_runs?.length ?? 0) > 0 ? 5000 : 30000),
  });
  const data = query.data;
  const lineJudge = data?.evaluators.find((e) => e.key === 'tutor_line_quality');

  return (
    <EvalShell
      title="Dashboard"
      subtitle="Scores against baseline, live runs, and judge agreement."
      actions={<NewExperimentDialog />}
    >
      {!data ? (
        <div className="grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-72 rounded-2xl" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat
              label="Sessions under test"
              value={String(data.fixtures.length)}
              sub={`${data.fixtures.filter((f) => f.baseline).length} with a baseline`}
              to="/evals/sessions"
            />
            <Stat
              label="Running now"
              to="/evals/runs"
              value={String(data.active_runs.length)}
              sub={data.runner?.online ? 'runner online' : 'runner offline'}
            />
            <Stat
              label="Lines labeled"
              value={`${data.labeled_lines ?? 0}`}
              sub={`of ${data.tutor_lines ?? 0} tutor lines`}
              to="/evals/label"
            />
            <Stat
              label="Judge agreement"
              value={pct(lineJudge?.alignment?.agreement)}
              sub={lineJudge?.alignment?.compared ? `on ${lineJudge.alignment.compared} lines you labeled` : 'label lines to measure it'}
              to={lineJudge ? `/evals/evaluators/${lineJudge.id}` : undefined}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {data.fixtures.map((card) => (
              <FixtureCard key={card.fixture.id} card={card} />
            ))}
          </div>

          {data.active_runs.length > 0 && (
            <Panel
              title={`Live now · ${data.active_runs.length}`}
              aside={
                <Link to="/evals/runs" search={{ status: 'live', page: 1 }} className="text-[12px] font-medium text-neutral-700 hover:underline">
                  All runs
                </Link>
              }
            >
              <div className="grid gap-3 md:grid-cols-3">
                {data.active_runs
                  .filter((r) => r.status === 'running')
                  .slice(0, 3)
                  .map((run) => (
                    <LiveRunTile key={run.id} run={run} compact />
                  ))}
              </div>
            </Panel>
          )}
        </>
      )}
    </EvalShell>
  );
}

function FixtureCard({ card }: { card: EvalFixtureCard }) {
  const name = useEvaluatorName();
  const history = card.history ?? [];
  const latest = history[history.length - 1];
  const baseline = card.baseline;
  const scores = latest?.evaluator_scores ?? baseline?.evaluator_scores ?? {};
  const weakest = Object.entries(scores)
    .sort(([, a], [, b]) => a - b)
    .slice(0, 4);
  const runsOf = baseline ?? latest;
  // Deltas only mean something once a later experiment differs from the baseline.
  const compareTo = latest && baseline && latest.experiment_id !== baseline.experiment_id ? baseline : undefined;

  return (
    <section className="flex flex-col rounded-2xl bg-white p-4 ring-1 ring-black/5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Cover url={card.fixture.cover_url} size={44} />
          <div className="min-w-0">
            <p className="truncate text-[15px] font-semibold text-neutral-900">{card.fixture.name}</p>
            <p className="text-[12px] text-neutral-400">{card.fixture.use_case || 'session'}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Baseline</p>
          <p className="text-[26px] font-semibold leading-none tracking-tight text-neutral-900">{pct(baseline?.score)}</p>
        </div>
      </div>
      <p className="mt-2 text-[12px] text-neutral-500">
        {latest ? (
          <>
            Latest <span className="font-medium text-neutral-700">{latest.name}</span> · {pct(latest.score)}{' '}
            <Delta value={latest.score} baseline={compareTo?.score} />
          </>
        ) : (
          'Not run yet'
        )}
      </p>
      <div className="mt-3">
        <ScoreHistoryChart history={history} baseline={baseline?.score} />
      </div>
      {weakest.length > 0 && (
        <div className="mt-3 space-y-1">
          <p className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Weakest checks</p>
          {weakest.map(([key, value]) => (
            <div key={key} className="flex items-center justify-between gap-2 text-[12px]">
              <span className="truncate text-neutral-600">{name(key)}</span>
              <span className="flex items-center gap-1.5">
                <span className={cn('rounded-full px-1.5 py-0.5 text-[11px] font-medium', scoreTone(value))}>{pct(value)}</span>
                <Delta value={value} baseline={compareTo?.evaluator_scores?.[key]} />
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-auto flex gap-3 pt-3 text-[12px]">
        {runsOf && (
          <Link
            to="/evals/experiments/$experimentId"
            params={{ experimentId: runsOf.experiment_id }}
            className="font-medium text-neutral-700 hover:underline"
          >
            {baseline ? 'Baseline runs' : 'Latest runs'}
          </Link>
        )}
        {!baseline && history.length > 0 && <span className="text-amber-700">No baseline set yet</span>}
      </div>
    </section>
  );
}
